import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import config from '../../../utils/config';
import NewLineFormWrapper from './NewLineFormWrapper';
import { useAuth } from '../../../wms/context/wms-context';
import Notification from '../../../components/Notification';
import logError from '../../../utils/errorUtil';
import LoadingSpinner from '../../../components/LoadingSpinner';
import CreateNewPartModal from './CreatePartModal';

export default function ReceiveNewLineModal({ status, setStatus, selectedMaid }) {
    const { currentUser, setCurrentUser } = useAuth();
    const ORIGIN = config.origin;
    const [formData, setFormData] = useState([]);
    const [allMaids, setAllMaids] = useState([]);
    const [selectedMaidMultiple, setSelectedMaidMultiple] = useState(null);
    const [multiInitLoading, setMultiInitLoading] = useState(false);

    const [showNotification, setShowNotification] = useState(false);
    const [notificationDetails, setNotificationDetails] = useState({});
    const [notificationMessage, setNotificationMessage] = useState({});
    const [pendingOpenReceiving, setPendingOpenReceiving] = useState(false);
    const didInitDefaultRef = useRef(false);

    const [showNewPartModal, setShowNewPartModal] = useState(false);
    const [newPartDefaultName, setNewPartDefaultName] = useState('');

    const handleMaidChange = (event) => {
        const id = event.target.value;
        const maidData = allMaids.find((m) => m.Id === id) || null;

        // Manifest Name is required to receive against a MAID; we already know it here, so just notify and stop.
        if (maidData && !maidData.inscor__Manifest_Name__c) {
            setShowNotification(true);
            setNotificationMessage({
                title: 'Manifest Error',
                desc: 'Manifests "Manifest Name" field is not filled out. Please fill this out in order to receive against this MAID.',
                status: 'error',
            });
            return;
        }

        setSelectedMaidMultiple(maidData);
    };

    useEffect(() => {
        getAllMaids();
    }, []);

    useEffect(() => {
        if (selectedMaid?.Id === 'multiple') {
            setMultiInitLoading(true);
        } else {
            setMultiInitLoading(false);
        }
    }, [selectedMaid?.Id]);

    useEffect(() => {
        if (selectedMaid?.Id !== 'multiple') return;
        if (!Array.isArray(allMaids) || allMaids.length === 0) return;

        if (selectedMaidMultiple?.Id) {
            setMultiInitLoading(false);
            return;
        }
        if (didInitDefaultRef.current) return;

        didInitDefaultRef.current = true;

        const savedId = allMaids.find((m) => m.Receiving_Default_Manifest__c === 1)?.Id;
        const saved = savedId && allMaids.find((m) => m.Id === savedId);
        const next = saved || null;

        // Skip auto-selecting a default manifest that has no Manifest Name, since it can't be received against.
        if (next && next.inscor__Manifest_Name__c && next.Id !== selectedMaidMultiple?.Id) {
            setSelectedMaidMultiple(next);
        }

        setMultiInitLoading(false);
    }, [selectedMaid?.Id, allMaids, selectedMaidMultiple?.Id]);

    useEffect(() => {
        if (!pendingOpenReceiving) return;
        const manifestIdInCtx = currentUser?.selectedMaid?.inscor__Manifest__c;
        if (manifestIdInCtx) {
            setStatus((s) => ({ ...s, receiveNewLinePanel: false, receivingPanel: true }));
            setPendingOpenReceiving(false);
        }
    }, [pendingOpenReceiving, currentUser?.selectedMaid?.inscor__Manifest__c, setStatus]);

    const handleSubmit = () => {
        if (selectedMaid?.Id === 'multiple' && !selectedMaidMultiple?.Id) {
            setShowNotification(true);
            setNotificationMessage({
                title: 'Select a Manifest',
                desc: 'Please pick a manifest from the list before continuing.',
                status: 'error',
            });
            return;
        }

        const missing = formData.find((f) => f.required && (f.value === undefined || f.value === ''));
        if (missing) {
            setShowNotification(true);
            setNotificationMessage({
                title: 'Error',
                desc: `Please fill out all required fields.`,
                status: 'error',
            });
            return;
        }

        const manifestId = selectedMaid?.Id === 'multiple' ? selectedMaidMultiple?.Id : selectedMaid?.Id;

        const selectedWarehouse = formData[3]?.value;
        const selectedWarehouseLocation = formData[3]?.dropdownData?.find((w) => w.Id === selectedWarehouse);
        const ownerCode = formData[1]?.dropdownData?.find((o) => o.Id === formData[1]?.value)?.Name;

        setCurrentUser((u) => ({
            ...u,
            selectedMaidLine: null,
            selectedMaid: {
                ...(selectedMaid?.Id === 'multiple' ? selectedMaidMultiple : selectedMaid),
                inscor__Company_Location__c: selectedWarehouseLocation?.inscor__Company_Location__c,
                inscor__Condition_Code__c: formData[0]?.value,
                inscor__Owner_Code__c: formData[1]?.value,
                inscor__Part_Master__c: formData[2]?.value,
                inscor__Warehouse__c: formData[3]?.value,
                inscor__Manifest__c: manifestId,
                inscor__Serialized__c: u.inscor__Serialized__c,
                Id: null,
            },
            receivingFormData: {
                ConditionCode: formData[0]?.valueName,
                OwnerCode: ownerCode,
                PartNumber: formData[2]?.valueName,
            },
        }));

        setPendingOpenReceiving(true);
    };

    const getAllMaids = () => {
        const dataToSend = {
            multi: true,
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
        };
        fetch(`${ORIGIN}/wms/receiving/get-manifests`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend),
        })
            .then((res) => {
                if (!res.ok) {
                    if (res.status === 500) {
                        return res.json().then((errorData) => {
                            setStatus({
                                ...status,
                                loading: false,
                                error: true,
                                errorMessage: errorData.message || 'Internal Server Error',
                            });
                        });
                    }
                }
                if (res.ok && res.headers.get('content-type')?.includes('application/json')) {
                    return res.json();
                } else {
                    return {
                        error: 'Internal Server Error',
                    };
                }
            })
            .then((data) => {
                if (data?.error === 'Internal Server Error') {
                    setNotificationDetails({
                        title: 'Internal Server Error',
                        desc: 'Please log out and log back in.',
                        status: 'error',
                    });
                    setShowNotification(true);
                    setStatus({ ...status, loading: false, receivingPanel: false });
                    return;
                }
                setAllMaids(data);
            })
            .catch((err) => {
                logError(err, 'getAllMaids');
                return [];
            });
    };

    const showMultiSpinner = selectedMaid?.Id === 'multiple' && (multiInitLoading || allMaids?.length === 0);

    // When adding new part reset formData and show modal
    useEffect(() => {
        const part = formData[2];
        if (part?.valueName == 'add_new') {
            let resetFormData = formData;
            resetFormData[2] = { ...part, valueName: '' };
            setFormData(resetFormData);
            setShowNewPartModal(true);
        }
    }, [formData]);

    return (
        <>
            <Notification
                showSavedNotification={showNotification}
                setShowSavedNotification={setShowNotification}
                title={notificationMessage.title}
                desc={notificationMessage.desc}
                status={notificationMessage.status}
            />

            <Transition.Root show={status.receiveNewLinePanel} as={Fragment}>
                <Dialog
                    as='div'
                    className='overflow-visible relative z-10'
                    onClose={() => setStatus({ ...status, receiveNewLinePanel: false })}
                >
                    <Transition.Child
                        as={Fragment}
                        enter='ease-out duration-300'
                        enterFrom='opacity-0'
                        enterTo='opacity-100'
                        leave='ease-in duration-200'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                    >
                        <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
                    </Transition.Child>

                    <div className='overflow-y-auto fixed inset-0 z-10 w-screen'>
                        <div className='flex justify-center items-end p-4 min-h-full text-center sm:items-center sm:p-0'>
                            <Transition.Child
                                as={Fragment}
                                enter='ease-out duration-300'
                                enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                                enterTo='opacity-100 translate-y-0 sm:scale-100'
                                leave='ease-in duration-200'
                                leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                                leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                            >
                                <Dialog.Panel className='overflow-visible relative px-4 pt-5 pb-4 text-left bg-white rounded-lg shadow-xl transition-all transform sm:my-8 sm:w-full sm:max-w-xl sm:p-6'>
                                    {selectedMaid?.Id === 'multiple' &&
                                        (showMultiSpinner ? (
                                            <div className='flex justify-center py-6'>
                                                <LoadingSpinner />
                                            </div>
                                        ) : (
                                            <form className='flex flex-col mb-4 ml-10'>
                                                <label className='block mb-2 ml-1 text-sm font-medium text-gray-700'>
                                                    Manifest
                                                </label>
                                                <select
                                                    disabled={status.loading}
                                                    className='p-2 w-48 rounded border disabled:opacity-50'
                                                    value={selectedMaidMultiple?.Id ?? ''}
                                                    onChange={handleMaidChange}
                                                >
                                                    <option value='' disabled>
                                                        Select a Manifest
                                                    </option>
                                                    {allMaids.map((maid) => (
                                                        <option key={maid.Id} value={maid.Id}>
                                                            {maid.Name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </form>
                                        ))}

                                    {!(selectedMaid?.Id === 'multiple' && showMultiSpinner) &&
                                        (selectedMaid?.Id !== 'multiple' || selectedMaidMultiple?.Id) && (
                                            <NewLineFormWrapper
                                                key={
                                                    selectedMaid?.Id === 'multiple'
                                                        ? selectedMaidMultiple?.Id || 'pending'
                                                        : selectedMaid?.Id
                                                }
                                                status={status}
                                                setStatus={setStatus}
                                                formData={formData}
                                                setFormData={setFormData}
                                                selectedManifest={selectedMaid}
                                                selectedMultipleManifest={selectedMaidMultiple}
                                                setNewPartDefaultName={setNewPartDefaultName}
                                            />
                                        )}

                                    <div className='mt-5 sm:mt-4 sm:flex sm:flex-row-reverse'>
                                        <button
                                            type='button'
                                            className='inline-flex justify-center px-3 py-2 w-full text-sm font-semibold text-white rounded-md shadow-sm bg-blue-primary hover:bg-blue-secondary sm:ml-3 sm:w-auto'
                                            onClick={handleSubmit}
                                            disabled={
                                                (selectedMaid?.Id === 'multiple' &&
                                                    (!selectedMaidMultiple?.Id || showMultiSpinner)) ||
                                                formData.find((f) => f.required && !f.value)
                                            }
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='inline-flex justify-center px-3 py-2 mt-3 w-full text-sm font-semibold text-gray-900 bg-white rounded-md ring-1 ring-inset ring-gray-300 shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto'
                                            onClick={() => setStatus({ ...status, receiveNewLinePanel: false })}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>

                    <CreateNewPartModal
                        showNewPartModal={showNewPartModal}
                        setShowNewPartModal={setShowNewPartModal}
                        status={status}
                        setStatus={setStatus}
                        setNotificationMessage={setNotificationMessage}
                        setShowNotification={setShowNotification}
                        formData={formData}
                        newPartDefaultName={newPartDefaultName}
                    />
                </Dialog>
            </Transition.Root>
        </>
    );
}
